import { Component, ComponentInterface, Element, h, Host, JSX, Listen, Method, Prop, State, Event, EventEmitter } from '@stencil/core';
import { transformTagNames } from '../../../global/transform-tag-name';
import { SharedDirectionService } from '../../../global/init-services';
import { MediaQueryService, MediaQueryChangeEvent } from '../../../utils/services/media-query.service';
import { ModalVariant, Size } from '../modal.types';
import { Level } from './modal-level.types';
import { IS_VISIBLE, IS_ENTERING, IS_LEAVING, IS_HIDDEN, HEIGHT_TRANSITION, FADE_IN, FADE_OUT, IS_FULL_HEIGHT } from './modal-level.constants';

/**
 * @slot - the contents of the modal-level
 * @slot footer - the contents of the modal-levels footer (usually `<wb-button>` elements)
 */
@Component({
  tag: 'wb-modal-level',
  styleUrl: 'scss/modal-level.scss',
  shadow: true,
})
export class ModalLevelComponent implements ComponentInterface {
  private TagNames = transformTagNames(this, 'wb-modal-level', ['wb-button', 'wb-round-button', 'wb-icon', 'wb-text', 'wb-heading']);

  private innerEl: HTMLDivElement;
  private headerEl: HTMLElement;
  private contentEl: HTMLElement;
  private footerEl: HTMLDivElement;

  @Element()
  public el: HTMLElement;

  /** @internal Modal variant to display */
  @Prop({ reflect: true })
  public variant!: ModalVariant;

  /** @internal Modal size */
  @Prop({ reflect: true })
  public size: Size;

  /** Defines the hierarchical layer for this element */
  @Prop({ reflect: true })
  public level?: Level = 1;

  /** Label of the back button (only shown when `showBackButton` is true) */
  @Prop()
  public backButtonLabel: string;

  /** Offers the option to hide the close button */
  @Prop()
  public showCloseButton: boolean = true;

  /** Aria label for the modal close button */
  @Prop()
  public closeButtonLabel!: string;

  /** Recommended for nested modals with level > 1 */
  @Prop()
  public showBackButton: boolean = false;

  /** The modal's headline */
  @Prop()
  public headline?: string;

  /** @internal if present sets the direction of the element to rtl */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  @State()
  public hasFooter: boolean = false;

  @State()
  private isDesktopView: boolean = false;

  /**
   * Event emitted when the back button is clicked on the level header.
   * Listening to this event and preventing its propagation will prevent the modal from changing levels.
   */
  @Event()
  public wbmodallevelbackclick!: EventEmitter<null>;

  /**
   * Event emitted when the close button is clicked on the level header.
   */
  @Event()
  public wbmodallevelcloseclick!: EventEmitter<null>;

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  @Listen('wbresize', { target: 'window' })
  public onMediaQueryChange(e: CustomEvent<MediaQueryChangeEvent>): void {
    this.isDesktopView = e.detail.current >= 'mq3';
  }

  /** @internal */
  @Method()
  public async show(): Promise<void> {
    this.innerEl.classList.add(IS_VISIBLE);
  }

  /** @internal */
  @Method()
  public async enter(): Promise<void> {
    this.innerEl.classList.remove(IS_ENTERING);
    this.innerEl.classList.add(IS_VISIBLE);
    await Promise.all([this.fadeInContent(), this.revealElement(this.headerEl), this.revealElement(this.footerEl)]);
  }

  /** @internal */
  @Method()
  public async leave(): Promise<void> {
    this.innerEl.classList.remove(IS_LEAVING);
    this.innerEl.classList.remove(IS_VISIBLE);

    this.setToInitialState();
  }

  /** @internal */
  @Method()
  public async prepareToEnter(): Promise<void> {
    this.innerEl.classList.add(IS_ENTERING);
    this.headerEl?.classList.add(IS_HIDDEN);
    this.footerEl?.classList.add(IS_HIDDEN);
  }

  /** @internal */
  @Method()
  public async prepareToLeave(): Promise<void> {
    await Promise.all([this.fadeOutContent(), this.hideElement(this.headerEl), this.hideElement(this.footerEl)]);
    this.innerEl.classList.add(IS_LEAVING);
  }

  /** @internal */
  @Method()
  public async expandToFullHeight(): Promise<void> {
    this.innerEl.classList.add(IS_FULL_HEIGHT);
  }

  /** @internal */
  @Method()
  public expandToHeight(height: number): Promise<void> {
    return new Promise((resolve) => {
      const currentHeight = this.innerEl.clientHeight;

      if (currentHeight === height) {
        resolve();
        return;
      }

      const onHeightChangeEnd = (event) => {
        if (event.target !== this.innerEl || event.propertyName !== 'height') {
          return;
        }

        this.innerEl.removeEventListener('transitionend', onHeightChangeEnd);
        this.innerEl.classList.remove(HEIGHT_TRANSITION);
        resolve();
      };

      requestAnimationFrame(() => {
        this.innerEl.style.height = `${currentHeight}px`;
        this.innerEl.classList.add(HEIGHT_TRANSITION);

        requestAnimationFrame(() => {
          this.innerEl.addEventListener('transitionend', onHeightChangeEnd);
          this.innerEl.style.height = `${height}px`;
        });
      });
    });
  }

  /** @internal */
  @Method()
  public async getInnerHeight(): Promise<number> {
    return this.innerEl.clientHeight;
  }

  public connectedCallback(): void {
    this.updateDirection();
    this.isDesktopView = new MediaQueryService(window).getCurrentMediaQuery() >= 'mq3';
  }

  public componentWillRender(): void {
    this.setHasFooter();
  }

  private setHasFooter = (): void => {
    this.hasFooter = Boolean(this.el.querySelector('[slot="footer"]'));
  };

  /** Used to fade in the content (with animation) */
  private fadeInContent(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.contentEl || this.contentEl.classList.contains(FADE_IN)) {
        resolve();
        return;
      }

      this.contentEl.addEventListener('animationend', () => resolve(), { once: true });
      this.contentEl.classList.add(FADE_IN);
      this.contentEl.classList.remove(FADE_OUT);
    });
  }

  /** Used to fade out the content (with animation) */
  private fadeOutContent(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.contentEl || this.contentEl.classList.contains(FADE_OUT)) {
        resolve();
        return;
      }

      this.contentEl.addEventListener('animationend', () => resolve(), { once: true });
      this.contentEl.classList.add(FADE_OUT);
      this.contentEl.classList.remove(FADE_IN);
    });
  }

  /** Used to hide the footer or header element (with transition) */
  private hideElement(el: HTMLElement): Promise<void> {
    return new Promise((resolve) => {
      if (!el) {
        resolve();
        return;
      }

      el.addEventListener('transitionend', () => resolve(), { once: true });
      el.classList.add(IS_HIDDEN);
      el.classList.remove(IS_VISIBLE);
    });
  }

  /** Used to reveal the footer or header element (with transition) */
  private revealElement(el: HTMLElement): Promise<void> {
    return new Promise((resolve) => {
      if (!el || el.classList.contains(IS_VISIBLE)) {
        resolve();
      }

      el.addEventListener('transitionend', () => resolve(), { once: true });
      el.classList.add(IS_VISIBLE);
      el.classList.remove(IS_HIDDEN);
    });
  }

  private onBackButtonClick = (): void => {
    this.wbmodallevelbackclick.emit();
  };

  private onCloseButtonClick = (): void => {
    this.wbmodallevelcloseclick.emit();
  };

  private setToInitialState(): void {
    // reset everything to its initial state
    this.innerEl.classList.remove(IS_FULL_HEIGHT);
    this.innerEl.style.height = null;

    this.contentEl?.classList.remove(FADE_OUT);
    this.headerEl?.classList.remove(IS_HIDDEN);
    this.footerEl?.classList.remove(IS_HIDDEN);
  }

  private setInnerRef = (ref): void => {
    this.innerEl = ref;
  };

  private setHeaderRef = (ref): void => {
    this.headerEl = ref;
  };

  private setContentRef = (ref): void => {
    this.contentEl = ref;
  };

  private setFooterRef = (ref): void => {
    this.footerEl = ref;
  };

  /** Emitted  when the modal is scrolled */
  @Event()
  public wbscroll: EventEmitter<void>;

  /** Emitted  when the modal is scrolled */
  @Event()
  public scroll: EventEmitter<void>;

  /**
   * Handles the scroll event.
   * @param event The scroll event.
   */
  private handleScroll = (event: Event): void => {
    event.stopPropagation();
    this.scroll.emit();
    this.wbscroll.emit();
  };

  public render(): JSX.Element {
    const { WbButton, WbRoundButton, WbText, WbHeading, WbIcon } = this.TagNames;
    const showHeader = this.variant !== 'prompt';

    return (
      <Host class="wb-modal-level">
        <div class={{ inner: true, 'has-footer': this.hasFooter }} ref={this.setInnerRef}>
          {showHeader && (
            <div class="header" part="header" ref={this.setHeaderRef}>
              {this.showBackButton && (
                <WbButton variant="tertiary" size="s" class="back-button" onClick={this.onBackButtonClick}>
                  <WbIcon slot="icon" name="chevron-left/24" flip-rtl />
                  {this.backButtonLabel}
                </WbButton>
              )}
              {this.showCloseButton && (
                <WbRoundButton data-testid="wb-modal-close-button" size={`new-${this.isDesktopView ? 'l' : 'm'}`} class="close-button" onClick={this.onCloseButtonClick}>
                  <WbIcon name="close/24" />
                  {this.closeButtonLabel}
                </WbRoundButton>
              )}
            </div>
          )}

          <WbText size="l" tag="div" class="content" part="content" tabindex="0" ref={this.setContentRef} onScroll={this.handleScroll}>
            {this.headline && (
              <WbHeading size="m" tag="h3" class="headline">
                {this.headline}
              </WbHeading>
            )}
            <slot />
          </WbText>

          {this.hasFooter && (
            <div class="footer" part="footer" ref={this.setFooterRef}>
              <slot name="footer" onSlotchange={this.setHasFooter} />
            </div>
          )}
        </div>
      </Host>
    );
  }
}
